import { t } from 'i18next';
import { uniq } from 'lodash';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import STable, { TableAction } from 'Components/structural/STable/STable';
import { Header, HeaderType, TABLE_ACTION_TYPES } from 'Components/structural/STable/types';
import {
  selectEntityPlayers,
  selectEntityPlayersMetadata,
} from 'Store/entities/entity-players/entity-players.selector';

import type { AudienceScope, EntityType } from '../AudienceGroupList/types';
import { useAudiencePlayersSearch } from './hooks/useAudiencePlayersSearch';

export type AudiencePlayerListProps = {
  entityId: number;
  entityType: EntityType;
  scope?: AudienceScope;
  audienceType?: 'magic-code' | 'email-list' | 'segmentation' | 'campaign';
  additionalEmails?: string[];
  removedEmails?: string[];
  deletePlayer?: (email: string) => void;
  deleteAdditionalPlayer?: (player: { id: string }) => void;
  audienceHasBeenReset?: boolean;
  loading?: boolean;
};

const noop = () => Promise.resolve();

export const AudiencePlayerList: FC<AudiencePlayerListProps> = ({
  entityId,
  entityType,
  scope,
  audienceType,
  additionalEmails = [],
  removedEmails = [],
  deleteAdditionalPlayer,
  deletePlayer,
  audienceHasBeenReset = false,
  loading: loadingFromProps = false,
}) => {
  const activityName =
    scope === 'campaign' ? t('activities:audience.campaign_scope') : t('activities:audience.breaking_news_scope');

  const noticeByMode = useMemo(
    () =>
      ({
        'magic-code': () => t('activities:audience.players.magic_code_notice', { activity: activityName }),
        'email-list': () => t('activities:audience.players.email_list_notice', { activity: activityName }),
        segmentation: () => t('activities:audience.players.segmentation_notice', { activity: activityName }),
        campaign: () => t('activities:audience.players.campaign_notice'),
      } as const),
    [],
  );

  const playersFromApi = useSelector(selectEntityPlayers(entityId));
  const meta = useSelector(selectEntityPlayersMetadata(entityId));
  const { total: totalFromApi, loading } = meta;

  const { setNameFilter, fetchNextPlayers } = useAudiencePlayersSearch({
    audienceHasBeenReset,
    entityId,
    entityType,
  });

  // If the audience has been reset, we don't want to display the players that were fetched before the reset
  const players =
    audienceHasBeenReset || loading ? [] : playersFromApi.filter((player) => !removedEmails.includes(player.email));
  const total = audienceHasBeenReset || loading ? 0 : totalFromApi;

  const title = t('activities:audience.players.title', { count: total, activity: activityName });

  const handleShowMore = () => {
    if (total <= players.length && !loading) {
      return;
    }

    fetchNextPlayers();
  };

  const tableActions = useMemo((): TableAction[] => {
    const handleSearch = (name?: string) => {
      setNameFilter(name || '');
    };

    return [
      {
        type: TABLE_ACTION_TYPES.SEARCH,
        id: 'table-action-1',
        params: {
          onClick: noop,
          onRequestEnd: noop,
          placeholder: t('activities:audience.players.search_placeholder'),
          placeholderOnExpand: t('activities:audience.players.search_placeholder_on_expand'),
          onAction: handleSearch,
        },
      },
    ];
  }, [setNameFilter]);

  const handleDeletePlayer = (player: { id: number }) => {
    const playerToDelete = players.find(({ id }) => id === player.id);
    if (!playerToDelete || !deletePlayer) return;
    deletePlayer(playerToDelete.email);
  };

  const additionalEmailsData = useMemo(() => {
    return uniq(additionalEmails).map((email) => {
      return {
        id: email,
        profilePicture: {
          id: `${email}_profilePicture`,
          type: 'UTableCellProfilePicture',
          params: {
            picture: '',
          },
          wrapperStyle: {
            display: 'flex',
            flex: 1,
          },
        },
        lastName: {
          id: `${email}_lastName`,
          type: 'UTableCellUser',
          params: {
            name: '',
            email: email,
            isValidated: false,
          },
        },
        createdAt: {
          id: `${email}_createdAt`,
          type: 'UTableCellText',
          params: {
            value: '',
            type: 'date',
          },
          wrapperStyle: {
            display: 'flex',
            flex: 1,
          },
        },
        segmentation: {
          id: `${email}_segmentation`,
          type: 'UTableCellSegment',
          params: {
            items: [],
          },
        },
        callbackOptions: [
          {
            icon: 'delete',
            id: 'action_delete_id',
            callback: deleteAdditionalPlayer ?? noop,
          },
        ],
      };
    });
  }, [additionalEmails]);

  const tableColumns = useMemo<Header[]>(() => {
    return [
      {
        id: '0',
        type: HeaderType.EmptyHeader,
        width: '44px',
        disableSorting: true,
        accessor: 'profilePicture',
      },
      {
        id: '1',
        type: HeaderType.CellHeader,
        headerParams: {
          text: t('activities:audience.players.username'),
          align: 'left',
          onAction: noop,
        },
        width: '180px',
        accessor: 'lastName',
      },
      {
        id: '2',
        type: HeaderType.CellHeader,
        headerParams: {
          text: t('activities:audience.players.creation'),
          align: 'center',
          onAction: noop,
        },
        width: '90px',
        accessor: 'createdAt',
      },
      {
        id: '3',
        type: HeaderType.CellHeader,
        headerParams: {
          text: t('activities:audience.players.segments'),
          align: 'left',
          onAction: noop,
        },
        disableSorting: true,
        accessor: 'segmentation',
      },
    ];
  }, []);

  const data: any = players.map(({ id, ...player }) => {
    return {
      id: id,
      profilePicture: {
        id: `${id}_profilePicture`,
        type: 'UTableCellProfilePicture',
        params: {
          picture: player?.profilePicture?.url || '',
        },
        wrapperStyle: {
          display: 'flex',
          flex: 1,
        },
      },
      lastName: {
        id: `${id}_lastName`,
        type: 'UTableCellUser',
        params: {
          name: `${player.lastName || ''} ${player.firstName || ''}`.trim(),
          email: player.email,
          isValidated: player.activated,
        },
      },
      createdAt: {
        id: `${id}_createdAt`,
        type: 'UTableCellText',
        params: {
          value: player.createdAt || '',
          type: 'date',
        },
        wrapperStyle: {
          display: 'flex',
          flex: 1,
        },
      },
      segmentation: {
        id: `${id}_segmentation`,
        type: 'UTableCellSegment',
        params: {
          items: player.segments.map((segment) => {
            return {
              value: segment.label,
              color: segment.color,
            };
          }),
        },
      },
      callbackOptions: [
        {
          icon: 'delete',
          id: 'action_delete_id',
          callback: handleDeletePlayer ?? noop,
        },
      ],
    };
  });

  const tableMessage = additionalEmails.length
    ? {
        type: 'warning' as const,
        message: t('activities:audience.players.change_warning', { activity: activityName }),
      }
    : { type: 'warning' as const, message: '' };

  return (
    <STable
      title={title}
      columns={tableColumns}
      data={[...additionalEmailsData, ...data]}
      range={20}
      emptyRowText={t('activities:audience.players.empty_row')}
      showMoreText={t('activities:audience.players.show_more')}
      loadingText={t('activities:audience.players.loading')}
      total={total}
      tableActions={tableActions}
      isTableLoading={loadingFromProps || (players.length === 0 && loading)}
      onShowMoreClicked={handleShowMore}
      notice={audienceType ? noticeByMode[audienceType]() : ''}
      tableMessage={tableMessage}
    />
  );
};
